import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styles from "./examMarkEntry.module.css";
import { FaEye, FaPlus } from "react-icons/fa";
import CustomMRTTable from "../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../services/Api";
import {
  CustomButton,
  CustomDatePicker,
  CustomDropdown,
} from "../../../components";
import { fetchStaffClassAndSubjects } from "../../../redux/Masterdata";
import dayjs from "dayjs";
import moment from "moment";
import PopupModal from "../../../components/PopupModal/PopupModal";

function ExamMarkEntry() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const staff_id = clientData?.master_id;
  const academic_year_id = clientData?.academic_year_id || "";
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedExamName, setSelectedExamName] = useState("");
  const [formData, setFormData] = useState({
    dateOfExam: "",
  });
  const [examData, setExamData] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiSearch, setApiSearch] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [tableData, setTableData] = useState([]);
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  const { staffClassAndSubjectOptions: reduxClassOptions } = useSelector(
    (state) => state.masterData,
  );
  const examOptions = examData.map((item) => ({
    label: item.exam_type, // what shows in dropdown
    value: item.id, // what gets stored in formData
  }));

  // onChange
  const handleClassChange = (value) => {
    setSelectedClass(value);
    setSelectedSection("");

    setErrors((prev) => ({
      ...prev,
      class: "",
    }));
  };
  const handleSectionChange = (value) => {
    setSelectedSection(value);

    setErrors((prev) => ({
      ...prev,
      section: "",
    }));
  };
  const handleSubjectChange = (value) => {
    setSelectedSubject(value);

    setErrors((prev) => ({
      ...prev,
      subject: "",
    }));
  };
  const handleExamNameChange = (value) => {
    setSelectedExamName(value);

    setErrors((prev) => ({
      ...prev,
      examName: "",
    }));
  };
  const handleFilterDateChange = (name, date) => {
    setFormData((prev) => ({
      ...prev,
      [name]: date ? dayjs(date).format("YYYY-MM-DD") : null,
    }));
    setErrors((prev) => ({
      ...prev,
      dateOfExam: "",
    }));
  };

  const classOptions = useMemo(() => {
    const seen = new Set();
    return (reduxClassOptions || [])
      .filter((item) => {
        const key = String(item.class_id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((item) => ({
        value: String(item.class_id),
        label: item.class_name,
      }));
  }, [reduxClassOptions]);

  // ─── Section dropdown (filtered by selected class_id, deduplicated) ───
  const sectionOptions = useMemo(() => {
    if (!selectedClass) return [];
    const classId =
      typeof selectedClass === "object" ? selectedClass?.value : selectedClass;
    const seen = new Set();
    return (reduxClassOptions || [])
      .filter((item) => {
        if (String(item.class_id) !== String(classId)) return false;
        const key = String(item.section_id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((item) => ({
        value: String(item.section_id),
        label: String(item.section_id),
      }));
  }, [reduxClassOptions, selectedClass]);

  const subjectOptions = useMemo(() => {
    const seen = new Set();
    return (reduxClassOptions || [])
      .filter((item) => {
        if (seen.has(item.subject_id)) return false;
        seen.add(item.subject_id);
        return true;
      })
      .map((item) => ({
        value: item.subject_id,
        label: item.subject_name,
      }));
  }, [reduxClassOptions]);

  useEffect(() => {
    if (school_id) {
      fetchExamConfigList();
      handleSubmit("", true);
    }
  }, [school_id]);

  const fetchExamConfigList = async () => {
    setLoading(true);
    try {
      const payload = {
        school_id: parseInt(school_id),
        academic_year: parseInt(academic_year_id),
      };

      const response = await AxiosInstance.post(
        `/exam/examConfig/list`,
        payload,
      );

      if (response?.data?.status) {
        const examData = response.data.data || [];
        const transformedData = examData.map((item, index) => ({
          id: item.id,
          sno: index + 1,
          academic_id: item.academic_id,
          academic_year: item.academic_year,
          exam_type: item.exam_type,
          start_date: item.start_date
            ? dayjs(item.start_date).format("DD-MM-YYYY")
            : "",
          end_date: item.end_date
            ? dayjs(item.end_date).format("DD-MM-YYYY")
            : "",
          isActive: item.isActive,
        }));
        setExamData(transformedData);
      } else {
        setExamData([]);
      }
    } catch (error) {
      console.error("Error fetching exam config list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(
      fetchStaffClassAndSubjects({
        staff_id: staff_id,
        class_id: selectedClass?.value || "",
        section_id: selectedSection?.value || "",
        subject_id: selectedSubject?.value || "",
      }),
    );
  }, [dispatch, school_id, selectedClass, selectedSection, selectedSubject]);

  const validateForm = () => {
    const newErrors = {};
    if (!selectedClass) newErrors.class = translations.errors.classRequired;
    if (!selectedSection)
      newErrors.section = translations.errors.sectionRequired;
    if (!selectedSubject)
      newErrors.subject = translations.errors.subjectRequired;
    if (!selectedExamName)
      newErrors.examName = translations.errors.examNameRequired;

    return newErrors;
  };

  const handleCancel = () => {
    setSelectedClass("");
    setSelectedSection("");
    setSelectedSubject("");
    setSelectedExamName("");
    setFormData({});
  };
  const handleSubmit = async (e, skipValidation = false) => {
    if (e) e.preventDefault();

    if (!skipValidation) {
      const validationErrors = validateForm();
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length === 0) {
        const payload = {
          school_id: school_id,
          staff_id: staff_id,
          class_id: selectedClass.value || "",
          section_id: selectedSection.value || "",
          academic_id: parseInt(academic_year_id),
          subject_id: selectedSubject.value || "",
          config_id: selectedExamName.value || "",
          date: formData.dateOfExam
            ? moment(formData.dateOfExam).format("DD-MM-YYYY")
            : "" || "",
          page: String(pagination.pageIndex + 1),
          limit: String(pagination.pageSize),
        };
        const response = await AxiosInstance.post(
          `/exam/examMarkEntry/list`,
          payload,
        );
        if (response.data?.status) {
          setTableData(response.data.data);
          // setModalState({
          //   open: true,
          //   type: "success",
          //   message: response.data?.message,
          //   redirectPath: "",
          // });
        } else {
          setTableData([]);
          setModalState({
            open: true,
            type: "success",
            message: response.data?.message || "No Records",
            redirectPath: "",
          });
        }
      }
    } else {
      const payload = {
        school_id: school_id,
        staff_id: staff_id,
        academic_id: parseInt(academic_year_id),
        class_id: selectedClass.value || "",
        section_id: selectedSection.value || "",
        subject_id: selectedSubject.value || "",
        config_id: selectedExamName.value || "",
        date: formData.dateOfExam
          ? moment(formData.dateOfExam).format("DD-MM-YYYY")
          : "" || "",
        page: String(pagination.pageIndex + 1),
        limit: String(pagination.pageSize),
      };
      const response = await AxiosInstance.post(
        `/exam/examMarkEntry/list`,
        payload,
      );
      if (response.data?.status) {
        setTableData(response.data.data);
        // setModalState({
        //   open: true,
        //   type: "success",
        //   message: response.data?.message,
        //   redirectPath: "",
        // });
      } else {
        setTableData([]);
        setModalState({
          open: true,
          type: "success",
          message: response.data?.message || "No Records",
          redirectPath: "",
        });
      }
    }
  };
  const handleView = (rowData) => {
    // find any one record for this day (they all share same class/section/shift)

    navigate("/user/exams/viewMarks", {
      state: {
        CLASSID: rowData.class_id,
        SECTION: rowData.section_id,
        SUBJECTID: rowData.subject_id,
        MODEOFEXAM: rowData.modeof_exam,
        EXAMNAME: rowData.config_id,
      },
    });
  };
  const columns = useMemo(
    () => [
      {
        accessorKey: "class_name",
        header: translations.labels.class,
        muiFilterTextFieldProps: {
          placeholder: "Filter by Class",
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              setApiSearch(e.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }
          },
        },
      },
      {
        accessorKey: "section_id",
        header: translations.labels.section,
        muiFilterTextFieldProps: {
          placeholder: "Filter by Setion",
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              setApiSearch(e.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }
          },
        },
      },
      {
        accessorKey: "subject_name",
        header: translations.labels.subject,
        muiFilterTextFieldProps: {
          placeholder: "Filter by subject",
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              setApiSearch(e.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }
          },
        },
      },
      {
        accessorKey: "exam_type",
        header: translations.labels.examName,
        muiFilterTextFieldProps: {
          placeholder: "Filter by Chapter",
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              setApiSearch(e.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }
          },
        },
      },
      {
        accessorKey: "modeof_exam",
        header: translations.labels.modeOfExam,
        muiFilterTextFieldProps: {
          placeholder: "Filter by exam name",
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              setApiSearch(e.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }
          },
        },
      },
      {
        accessorKey: "exam_date",
        header: translations.labels.dateOfExam,
        muiFilterTextFieldProps: {
          placeholder: "Filter by Short exam date",
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              setApiSearch(e.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }
          },
        },
      },
      {
        accessorKey: "verify_marks",
        header: translations.labels.status || "Status",
        enableSorting: false,
        Cell: ({ row }) => {
          const isVerified = row.original.verify_marks === 1;
          return (
            <span
              className={
                isVerified ? styles.verifiedBadge : styles.notVerifiedBadge
              }
            >
              {isVerified
                ? translations.labels.verified || "Verified"
                : translations.labels.notVerified || "Not Verified"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: translations.labels?.actions || "Actions",
        enableSorting: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={translations.labels?.viewStudent || "View Student"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [],
  );

  const handleAdd = (data) => {
    navigate("/user/exams/CreateMarks");
  };
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;

    setModalState((prev) => ({
      ...prev,
      open: false,
    }));

    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>
            {" "}
            {translations?.labels?.examMarkEntry || "Exam Mark Entry"}
          </h1>
        </div>

        <div className="col-md-12 p-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <CustomButton
              variant="contained"
              onClick={handleAdd}
              className={styles.saveButton}
              fullWidth={false}
            >
              <FaPlus style={{ marginRight: "8px" }} />
              {translations?.labels?.add || "ADD"}
            </CustomButton>

            <CustomButton
              variant="contained"
              onClick={() => navigate("/user/exams/markBulkUpload")}
              className={styles.saveButton}
              fullWidth={false}
            >
              <FaPlus style={{ marginRight: "8px" }} />
              {translations?.labels?.markBulkUpload || "Mark Bulk Upload"}
            </CustomButton>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="row g-3 align-items-center">
          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels.class}
              name="classValue"
              value={selectedClass}
              onChange={handleClassChange}
              options={classOptions}
              placeholder={translations.labels.selectClass}
              required
              error={errors.class}
              isSearchable={false}
            />
          </div>

          {/* Section */}

          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels.section}
              name="section"
              value={selectedSection}
              options={sectionOptions}
              onChange={handleSectionChange}
              placeholder={translations.labels.selectSection}
              required
              error={errors.section}
              isSearchable={false}
            />
          </div>
          {/* Subject */}

          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels.subject}
              name="subject"
              value={selectedSubject}
              options={subjectOptions}
              onChange={handleSubjectChange}
              placeholder={translations.labels.selectsubjectPlaceholder}
              required
              error={errors.subject}
              isSearchable={false}
            />
          </div>

          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels.examName}
              name="examName"
              value={selectedExamName}
              onChange={handleExamNameChange}
              options={examOptions}
              placeholder={translations.labels.selectsubjectPlaceholder}
              required
              error={errors.examName}
              isSearchable={false}
            />
          </div>

          <div className="col-lg-2 px-2 col-md-2 col-sm-12">
            <CustomDatePicker
              label={translations.labels?.dateOfExam || "Date of Exam"}
              name="fromDate"
              value={formData.dateOfExam ? dayjs(formData.dateOfExam) : null}
              onChange={(date) => handleFilterDateChange("dateOfExam", date)}
              placeholder="Ex: 30/07/2016"
              error={errors.dateOfExam}
            />
          </div>

          <div className="col-md-2 px-2 d-flex align-items-end">
            <div className="d-flex gap-2 w-100">
              <CustomButton
                variant="contained"
                onClick={handleSubmit}
                className={styles.saveButton}
                loading={loading}
              >
                {translations.labels.searchButton}
              </CustomButton>

              <CustomButton
                variant="contained"
                onClick={handleCancel}
                className={styles.cancelButton}
                disabled={loading}
              >
                {translations.labels.resetButton}
              </CustomButton>
            </div>
          </div>
        </form>

        {tableData.length > 0 && (
          <>
            <CustomMRTTable
              columns={columns}
              data={tableData}
              loading={loading}
              pagination={pagination}
              // rowCount={rowCount}
              onPaginationChange={setPagination}
              // searchInput={searchInput}
              // setSearchInput={setSearchInput}
              onSearchEnter={(value) => {
                setApiSearch(value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              enableSearch={false}
              enableFiltering={false}
            />
          </>
        )}
      </div>
    </>
  );
}

export default ExamMarkEntry;
